import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { MagnifyingGlassIcon, EllipsisVerticalIcon, WalletIcon } from '@heroicons/react/16/solid'
import { EyeIcon, ArrowLeftIcon } from '@heroicons/react/20/solid'

import { Input, InputGroup } from 'components/input'
import { Divider } from 'components/divider'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'

import loadingImage from '../../assets/images/boat-loading.gif'
import { GET_USER_LIST } from 'graphql/users/queries'
import { getPageRange } from 'utils/paginationHelper'

export default function NauticalMilesWallets() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [filterObj, setFilterObj] = useState({}) // Remove active filter state
    const [searchString, setSearchString] = useState('')
    const searchStringRef = useRef(searchString)
    const [walletList, setWalletList] = useState(null)
    const [walletCount, setWalletCount] = useState(0)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(walletCount / rowsPerPage)

    const [getWalletList, { loading: walletListLoading, error: walletListError }] = useLazyQuery(GET_USER_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchUser.success && data.searchUser.code === '200') {
                setWalletList(data.searchUser.users)
                setWalletCount(data.searchUser.total_count)
            }
        },
    })

    useEffect(() => {
        searchStringRef.current = searchString
    }, [searchString])

    useEffect(() => {
        searchWallets(filterObj)
    }, [page, rowsPerPage])

    const searchWallets = filter => {
        getWalletList({
            variables: {
                page: {
                    offset: page * rowsPerPage,
                    limit: rowsPerPage,
                },
                userPool: 'app',
                searchString: searchStringRef.current,
            },
        })
    }

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    const onClickSearch = () => {
        setPage(0)
        setRowsPerPage(10)
        searchWallets(filterObj)
    }

    const onClickSearchClear = () => {
        if (searchString === '')
            return;
        setSearchString('')
        setFilterObj({})
        setPage(0)
        setRowsPerPage(10)
        setTimeout(() => {
            searchWallets({})
        }, 100)
    }

    return (
        <>
            {/* Header section */}
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            {/* Main content */}
            <div className="flex flex-wrap items-end justify-between gap-4">
                {/* Title and search section */}
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                        <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                                <WalletIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                            </div>
                            <div>
                                <span className="ms-3">{t('Manage Nautical Miles Wallets')}</span>
                                <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                    {t('View and manage all nautical miles wallet transactions')}
                                </p>
                            </div>
                        </h1>
                    </div>

                    {/* Search filters */}
                    <div className="mt-8 mb-4 flex sm:flex-row flex-wrap w-full gap-4">
                        <div className="flex-2">
                            <label className="block text-sm font-medium leading-6 text-gray-900">{t('Search')}</label>
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    name="search"
                                    value={searchString}
                                    onChange={e => {
                                        const maxSearchLen = 64;
                                        setSearchString(
                                            e.target.value.length <= maxSearchLen ? e.target.value : 
                                            e.target.value.substring(0, maxSearchLen)
                                        );
                                    }}
                                    placeholder={t('Search')}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            onClickSearch();
                                        } else if (e.key === 'Escape') {
                                            onClickSearchClear();
                                        }
                                    }}
                                />
                            </InputGroup>
                        </div>

                        <div className="flex-none grow gap-x-2 space-x-2 justify-end place-content-end">
                            <button
                                type="button"
                                onClick={onClickSearch}
                                className="inline-flex me-2 items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                type="button"
                                onClick={onClickSearchClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Divider />

            {/* Loading state */}
            {walletListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}

            {/* Table section */}
            {!walletListLoading && !walletListError && walletList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Name')}</TableHeader>
                                    <TableHeader>{t('Email')}</TableHeader>
                                    <TableHeader>{t('Phone Number')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {walletList.map(user => (
                                    <TableRow
                                        key={user.id}
                                        onClick={() =>
                                            navigate(`/nautical-miles-wallets/detail`, {
                                                state: { userData: user },
                                            })
                                        }
                                        className="cursor-pointer hover:bg-gray-50"
                                    >
                                        <TableCell>
                                            {user.user_info.given_name && user.user_info.family_name
                                                ? `${user.user_info.given_name} ${user.user_info.family_name}`
                                                : 'N/A'}
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.user_info.phone_number || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate('/nautical-miles-wallets/detail', {
                                                                state: { userData: user },
                                                            })
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}

            {/* No records found message */}
            {!walletListLoading && !walletListError && walletList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('Please add or modify your search criteria and try again.')}
                    </p>
                    <div className="mt-6"></div>
                </div>
            )}
        </>
    )
}
